// SPDX-License-Identifier: Apache-2.0
import IGirafeContext from '../../tools/context/icontext';
import BaseLayer from '../../models/layers/baselayer';
import LayerWms from '../../models/layers/layerwms';
import Feature from 'ol/Feature';
import SelectionParam from '../../models/selectionparam';
import { removeUnwantedOlParams } from '../../tools/utils/olutils';
import { WKT } from 'ol/format';
import CsvManager from '../../tools/export/csvmanager';
import ColumnAliasHelper from '../../tools/utils/aliases';

class LayerCsvExporter {
  private readonly context: IGirafeContext;
  private readonly csvManager: CsvManager;
  private readonly columnAliasHelper: ColumnAliasHelper;
  private readonly wktFormat = new WKT();

  private readonly WKT_DECIMALS = 2;
  public csvContentTruncated = false;

  constructor(context: IGirafeContext) {
    this.context = context;
    this.csvManager = new CsvManager(this.context);
    this.columnAliasHelper = new ColumnAliasHelper(this.context.stateManager);
  }

  public async createCsvFile(layer?: BaseLayer) {
    this.csvContentTruncated = false;
    if (!layer) {
      return;
    }
    if (layer instanceof LayerWms) {
      const features = await this.requestFeatures(layer);
      const { data, columnDefs } = this.featuresToColumnData(features, layer.name);
      const layerName = this.context.i18nManager.getTranslation(layer.name);
      this.csvManager.startDownload(data, columnDefs, `${layerName}-filter-result.csv`);
    }
  }

  private async requestFeatures(layer: LayerWms): Promise<Feature[]> {
    // Create a selection param object that defines the map max extent but nothing more.
    //  Any layer filter will be applied in the client automatically.
    const projection = this.context.mapManager.getMap().getView().getProjection();
    let extent = this.context.configManager.Config.map.maxExtent?.split(',').map(Number);
    extent ??= projection.getExtent();
    const selectionParam = new SelectionParam(layer.ogcServer, [layer], projection.getCode(), extent);

    if (layer.wfsQueryable) {
      const client = this.context.wfsManager.getClient(layer.ogcServer);
      return await client.getFeature(selectionParam).then((features) => {
        if (features.length === client.maxFeatures) {
          this.csvContentTruncated = true;
        }
        return features;
      });
    } else {
      return [];
    }
  }

  private featuresToColumnData(features: Feature[], layerName: string, includeGeometry: boolean = true) {
    const data: { [x: string]: unknown }[] = features.map((feature) => {
      const properties = removeUnwantedOlParams(feature, false);
      const geometry = feature.getGeometry();
      if (includeGeometry && geometry) {
        properties[feature.getGeometryName()] = this.wktFormat.writeGeometry(geometry, {
          decimals: this.WKT_DECIMALS
        });
      }
      return properties;
    });
    let columnDefs: { name: string }[] = [];
    if (data.length > 0) {
      columnDefs = Object.keys(data[0]).map((columnName) => {
        return {
          name: this.columnAliasHelper.getColumnAlias(layerName, columnName)
        };
      });
    }
    return { data, columnDefs };
  }
}

export default LayerCsvExporter;
